import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  ConversationAgent,
  ConversationMessage,
  ConversationTurnStatus,
  SessionInfo,
} from '@niuma/protocol';
import type { HubApi } from '../lib/api.js';
import type { ConnectionStatus } from '../lib/connection.js';
import type { SubmissionDisposition } from '../lib/conversation-submission.js';
import type { OutputBus } from '../lib/output-bus.js';
import { SessionComposer } from './SessionComposer.js';

interface Props {
  api: HubApi;
  bus: OutputBus;
  machineId: string;
  session: SessionInfo & { agent: ConversationAgent };
  transcriptId: string;
  connectionStatus: ConnectionStatus;
}

const PAGE_LIMIT = 40;
const TOP_LOAD_THRESHOLD = 32;
const BOTTOM_PIN_THRESHOLD = 80;
const LATEST_POLL_MS = 2000;

export function ConversationView({
  api,
  bus,
  machineId,
  session,
  transcriptId,
  connectionStatus,
}: Props): JSX.Element {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const pinnedRef = useRef(true);
  const refreshTimerRef = useRef<number | undefined>(undefined);
  const refreshInFlightRef = useRef(false);
  const pendingBottomScrollRef = useRef(false);
  const pendingTurnsRef = useRef<
    Array<{
      messageKey: string;
      sentAt: number;
      disposition: SubmissionDisposition;
      observedInTranscript: boolean;
      observedResponding: boolean;
    }>
  >([]);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [olderCursor, setOlderCursor] = useState<string | undefined>();
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pinnedToBottom, setPinnedToBottom] = useState(true);
  const [turnStatus, setTurnStatus] = useState<ConversationTurnStatus | 'unknown'>('unknown');

  const scrollToBottom = useCallback((): void => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const scroller = scrollerRef.current;
        if (!scroller) return;
        scroller.scrollTop = scroller.scrollHeight;
        pinnedRef.current = true;
        setPinnedToBottom(true);
      });
    });
  }, []);

  const updateTurnStatus = useCallback(
    (
      latestMessages: ConversationMessage[],
      latestStatus: ConversationTurnStatus | undefined,
    ): void => {
      const pendingTurns = pendingTurnsRef.current;
      if (pendingTurns.length === 0) {
        setTurnStatus(latestStatus ?? 'unknown');
        return;
      }

      const unmatchedUserMessages = latestMessages.filter((message) => message.role === 'user');
      for (const pending of pendingTurns) {
        const matchIndex = unmatchedUserMessages.findIndex(
          (message) =>
            messageContentKey(message) === pending.messageKey &&
            (message.timestamp === undefined || message.timestamp >= pending.sentAt - 2000),
        );
        if (matchIndex >= 0) {
          pending.observedInTranscript = true;
          unmatchedUserMessages.splice(matchIndex, 1);
        }
        if (latestStatus === 'responding' && pending.disposition !== 'queued') {
          pending.observedResponding = true;
        }
      }

      if (latestStatus === 'idle') {
        pendingTurnsRef.current = pendingTurns.filter(
          (pending) => !(pending.observedInTranscript || pending.observedResponding),
        );
      }

      if (pendingTurnsRef.current.length === 0) {
        setTurnStatus('idle');
        return;
      }

      setTurnStatus('responding');
    },
    [],
  );

  const refreshLatest = useCallback(async (): Promise<void> => {
    if (connectionStatus !== 'open') return;
    if (refreshInFlightRef.current) return;
    refreshInFlightRef.current = true;
    const shouldPin = pinnedRef.current || isNearBottom(scrollerRef.current);
    try {
      const page = await api.conversationPage(
        machineId,
        session.agent,
        transcriptId,
        session.cwd,
        undefined,
        PAGE_LIMIT,
      );
      updateTurnStatus(page.messages, page.turnStatus);
      setMessages((prev) => mergeLatestMessages(prev, page.messages));
      setOlderCursor((prev) => prev ?? page.olderCursor);
      setError(null);
      if (shouldPin) pendingBottomScrollRef.current = true;
    } catch (err) {
      if (!isConnectionError(err)) setError(err instanceof Error ? err.message : String(err));
    } finally {
      refreshInFlightRef.current = false;
    }
  }, [
    api,
    connectionStatus,
    machineId,
    session.agent,
    session.cwd,
    transcriptId,
    updateTurnStatus,
  ]);

  useEffect(() => {
    if (connectionStatus !== 'open') {
      setError(null);
      return;
    }
    void refreshLatest();
  }, [connectionStatus, refreshLatest]);

  useEffect(() => {
    if (!pendingBottomScrollRef.current) return;
    pendingBottomScrollRef.current = false;
    scrollToBottom();
  }, [messages.length, scrollToBottom]);

  useEffect(() => {
    let cancelled = false;
    setLoadingInitial(true);
    setError(null);
    setMessages([]);
    setOlderCursor(undefined);
    pendingTurnsRef.current = [];
    setTurnStatus('unknown');
    pinnedRef.current = true;
    setPinnedToBottom(true);

    if (connectionStatus !== 'open') {
      setLoadingInitial(false);
      return () => {
        cancelled = true;
      };
    }

    api
      .conversationPage(machineId, session.agent, transcriptId, session.cwd, undefined, PAGE_LIMIT)
      .then((page) => {
        if (cancelled) return;
        pendingBottomScrollRef.current = true;
        updateTurnStatus(page.messages, page.turnStatus);
        setMessages(page.messages);
        setOlderCursor(page.olderCursor);
      })
      .catch((err) => {
        if (cancelled) return;
        if (!isConnectionError(err)) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoadingInitial(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, machineId, scrollToBottom, session.agent, session.cwd, transcriptId, updateTurnStatus]);

  const loadOlder = useCallback(async (): Promise<void> => {
    if (!olderCursor || loadingOlder) return;
    const scroller = scrollerRef.current;
    const previousScrollHeight = scroller?.scrollHeight ?? 0;
    const previousScrollTop = scroller?.scrollTop ?? 0;

    setLoadingOlder(true);
    try {
      const page = await api.conversationPage(
        machineId,
        session.agent,
        transcriptId,
        session.cwd,
        olderCursor,
        PAGE_LIMIT,
      );
      setMessages((prev) => mergeOlderMessages(page.messages, prev));
      setOlderCursor(page.olderCursor);
      setError(null);
      window.requestAnimationFrame(() => {
        const current = scrollerRef.current;
        if (!current) return;
        current.scrollTop = current.scrollHeight - previousScrollHeight + previousScrollTop;
      });
    } catch (err) {
      if (!isConnectionError(err)) setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoadingOlder(false);
    }
  }, [api, loadingOlder, machineId, olderCursor, session.agent, session.cwd, transcriptId]);

  const handleScroll = useCallback((): void => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const pinned = isNearBottom(scroller);
    pinnedRef.current = pinned;
    setPinnedToBottom(pinned);
    if (scroller.scrollTop <= TOP_LOAD_THRESHOLD) void loadOlder();
  }, [loadOlder]);

  useEffect(() => {
    const unsubscribe = bus.subscribe(session.id, () => {
      if (refreshTimerRef.current !== undefined) window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = window.setTimeout(() => {
        refreshTimerRef.current = undefined;
        void refreshLatest();
      }, 1000);
    });

    return () => {
      if (refreshTimerRef.current !== undefined) window.clearTimeout(refreshTimerRef.current);
      unsubscribe();
    };
  }, [bus, refreshLatest, session.id]);

  useEffect(() => {
    const poll = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      void refreshLatest();
    }, LATEST_POLL_MS);

    return () => window.clearInterval(poll);
  }, [refreshLatest]);

  const handleSent = useCallback(
    (text: string, disposition: SubmissionDisposition): void => {
      const sentAt = Date.now();
      const optimistic: ConversationMessage = {
        id: `pending:${disposition}:${crypto.randomUUID()}`,
        role: 'user',
        text,
        timestamp: sentAt,
      };
      pendingTurnsRef.current.push({
        messageKey: messageContentKey(optimistic),
        sentAt,
        disposition,
        observedInTranscript: false,
        observedResponding: false,
      });
      setTurnStatus('responding');
      pendingBottomScrollRef.current = true;
      setMessages((prev) => [...prev, optimistic]);
      window.setTimeout(() => void refreshLatest(), 1200);
    },
    [refreshLatest],
  );

  return (
    <div className="conversation-wrap">
      <div className="conversation-scroll" ref={scrollerRef} onScroll={handleScroll}>
        {loadingOlder && <div className="conversation-status">加载中...</div>}
        {messages.map((message) => (
          <article className={`message ${message.role}`} key={message.id}>
            <div className="message-meta">
              <span>{message.role === 'assistant' ? session.agent : roleLabel(message.role)}</span>
              {message.timestamp && <time>{formatMessageTime(message.timestamp)}</time>}
              {pendingMessageLabel(message) && (
                <span className="message-delivery">{pendingMessageLabel(message)}</span>
              )}
            </div>
            <div className="message-text">{message.text}</div>
          </article>
        ))}
        {loadingInitial && <div className="conversation-status grow">加载中...</div>}
        {!loadingInitial && messages.length === 0 && (
          <div className="conversation-status grow">暂无可显示消息</div>
        )}
      </div>
      <div
        className={`conversation-turn-status ${connectionStatus === 'open' ? turnStatus : 'reconnecting'}`}
        role="status"
        aria-live="polite"
      >
        <span className="conversation-turn-indicator" aria-hidden="true" />
        <span>
          {connectionStatus === 'open'
            ? turnStatusLabel(turnStatus, messages.length > 0, queuedMessageCount(messages))
            : '连接恢复中…'}
        </span>
      </div>
      {error && <div className="conversation-error">{error}</div>}
      {!pinnedToBottom && (
        <button
          aria-label="滚到底部"
          className="scroll-jump conversation-jump"
          type="button"
          onClick={scrollToBottom}
        >
          ↓
        </button>
      )}
      <SessionComposer
        api={api}
        machineId={machineId}
        sessionId={session.id}
        agent={session.agent}
        turnStatus={turnStatus}
        onSent={handleSent}
      />
    </div>
  );
}

function turnStatusLabel(
  status: ConversationTurnStatus | 'unknown',
  hasMessages: boolean,
  queuedCount: number,
): string {
  if (status === 'responding') {
    return queuedCount > 0 ? `正在回复 · ${queuedCount} 条排队中` : '正在回复…';
  }
  if (status === 'idle') return hasMessages ? '回复已完成' : '等待你的消息';
  return '正在确认状态…';
}

function isConnectionError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    message === 'Connection closed' ||
    message === 'Connection interrupted' ||
    message === 'Connection unavailable'
  );
}

function mergeOlderMessages(
  older: ConversationMessage[],
  current: ConversationMessage[],
): ConversationMessage[] {
  const seen = new Set(current.map((message) => message.id));
  return [...older.filter((message) => !seen.has(message.id)), ...current];
}

function mergeLatestMessages(
  current: ConversationMessage[],
  latest: ConversationMessage[],
): ConversationMessage[] {
  const knownIds = new Set(
    current.filter((message) => !message.id.startsWith('pending:')).map((message) => message.id),
  );
  const availableLatest = latest.filter((message) => !knownIds.has(message.id));
  const merged = current.map((message) => {
    if (!message.id.startsWith('pending:')) return message;
    const matchIndex = availableLatest.findIndex(
      (candidate) => messageContentKey(candidate) === messageContentKey(message),
    );
    if (matchIndex < 0) return message;
    const replacement = availableLatest[matchIndex]!;
    availableLatest.splice(matchIndex, 1);
    return replacement;
  });
  const seen = new Set(merged.map((message) => message.id));
  return [...merged, ...availableLatest.filter((message) => !seen.has(message.id))];
}

function pendingMessageLabel(message: ConversationMessage): string | null {
  if (message.id.startsWith('pending:queued:')) return '已排队';
  if (message.id.startsWith('pending:steered:')) return '正在改向';
  if (message.id.startsWith('pending:sent:')) return '正在发送';
  return null;
}

function queuedMessageCount(messages: ConversationMessage[]): number {
  return messages.filter((message) => message.id.startsWith('pending:queued:')).length;
}

function messageContentKey(message: ConversationMessage): string {
  return `${message.role}\0${message.text}`;
}

function isNearBottom(scroller: HTMLElement | null): boolean {
  if (!scroller) return true;
  return scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight <= BOTTOM_PIN_THRESHOLD;
}

function roleLabel(role: ConversationMessage['role']): string {
  switch (role) {
    case 'user':
      return 'you';
    case 'system':
      return 'system';
    case 'assistant':
      return 'assistant';
    default:
      return 'message';
  }
}

function formatMessageTime(timestamp: number): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(timestamp);
}
